/**
 * useWatermarkHtmlImages — DOM-walk hook that swaps every <img> inside a
 * rendered HTML container with a watermarked blob URL from the backend
 * proxy. Applies to rich-text lesson content that comes back with
 * embedded images we don't own at render time.
 *
 * Usage:
 *   const ref = useRef(null);
 *   useWatermarkHtmlImages(ref, { lessonId });
 *   return <div ref={ref}><AutoTranslatedHtml html={...} /></div>;
 */

import { useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const reportAttempt = async (payload) => {
  try {
    const token = window.localStorage.getItem('token');
    if (!token) return;
    await axios.post(
      `${API_BASE}/api/content-protection/report-attempt`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch (_) { /* silent */ }
};

const useWatermarkHtmlImages = (containerRef, { lessonId, courseId } = {}) => {
  const { API } = useContext(AuthContext);

  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return undefined;

    const objectUrls = [];
    let cancelled = false;

    const swap = async () => {
      const imgs = Array.from(el.querySelectorAll('img'));
      for (const img of imgs) {
        // Skip already-processed images.
        if (img.dataset.fdaWatermarked === '1') continue;
        img.dataset.fdaWatermarked = '1';
        // Client-side hardening on every image.
        img.setAttribute('draggable', 'false');
        img.addEventListener('dragstart', (e) => e.preventDefault());
        img.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          toast.warning('Saving course images is disabled — see our Terms of Use.');
          reportAttempt({ lesson_id: lessonId, course_id: courseId, kind: 'image_right_click' });
        });

        const original = img.getAttribute('src');
        if (!original || original.startsWith('blob:') || original.startsWith('data:')) continue;

        try {
          const res = await axios.get(`${API}/content-protection/image`, {
            params: { url: original, lesson_id: lessonId },
            responseType: 'blob',
          });
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          objectUrls.push(url);
          img.src = url;
        } catch (_) {
          // Leave original in place if proxy fails — never break the lesson.
        }
      }
    };

    // Initial pass + a MutationObserver so images added later
    // (e.g. from AutoTranslated re-render) also get proxied.
    swap();
    const obs = new MutationObserver(() => { swap(); });
    obs.observe(el, { childList: true, subtree: true });

    return () => {
      cancelled = true;
      obs.disconnect();
      objectUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [containerRef, API, lessonId, courseId]);
};

export default useWatermarkHtmlImages;
